import React, {useEffect, useState} from 'react';
import {get, ref} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {Link} from "react-router-dom";

export default function AdminPlayers() {
    const {userRole} = useAuth();
    const [players, setPlayers] = useState([]);
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('totalScore');
    const [activeTab, setActiveTab] = useState('overview');
    const [quizPerformance, setQuizPerformance] = useState([]);
    const [categoryPerformance, setCategoryPerformance] = useState([]);
    const [error, setError] = useState('');

    // Récupération des données des joueurs depuis Firebase
    useEffect(() => {
        async function fetchData() {
            try {
                if (userRole !== 'admin') {
                    setError('Accès non autorisé. Vous devez être administrateur pour voir cette page.');
                    setIsLoading(false);
                    return;
                }

                // Récupérer les utilisateurs
                const usersRef = ref(database, 'users');
                const usersSnapshot = await get(usersRef);

                if (!usersSnapshot.exists()) {
                    setIsLoading(false);
                    return;
                }

                const usersData = usersSnapshot.val();
                const playersData = Object.entries(usersData)
                    .filter(([_, userData]) => userData.profile?.role === 'player')
                    .map(([id, userData]) => ({
                        id,
                        ...userData
                    }));

                setPlayers(playersData);
                setFilteredPlayers(playersData);

                // Calcul des statistiques pour les graphiques
                calculateQuizPerformance(playersData);
                calculateCategoryPerformance(playersData);
            } catch (error) {
                console.error("Erreur lors de la récupération des joueurs:", error);
                setError("Une erreur s'est produite lors du chargement des données des joueurs.");
            } finally {
                setIsLoading(false);
            }
        }

        fetchData();
    }, [userRole]);

    // Filtrer et trier les joueurs lorsque les critères changent
    useEffect(() => {
        let result = [...players];

        // Appliquer la recherche
        if (searchQuery) {
            result = result.filter(
                player =>
                    player.profile?.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    player.profile?.email?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Appliquer le tri
        result.sort((a, b) => {
            if (sortBy === 'name') {
                return (a.profile?.displayName || '').localeCompare(b.profile?.displayName || '');
            } else if (sortBy === 'totalGames') {
                return (b.gameHistory?.totalGames || 0) - (a.gameHistory?.totalGames || 0);
            } else if (sortBy === 'totalScore') {
                return (b.gameHistory?.totalScore || 0) - (a.gameHistory?.totalScore || 0);
            } else if (sortBy === 'lastPlayed') {
                const dateA = a.gameHistory?.lastPlayed ? new Date(a.gameHistory.lastPlayed) : new Date(0);
                const dateB = b.gameHistory?.lastPlayed ? new Date(b.gameHistory.lastPlayed) : new Date(0);
                return dateB - dateA;
            }
            return 0;
        });

        setFilteredPlayers(result);
    }, [players, searchQuery, sortBy]);

    // Calcul des données pour le graphique des performances par quiz
    const calculateQuizPerformance = (players) => {
        // Récupération des résultats par quiz
        const quizResults = {};

        players.forEach(player => {
            if (player.gameHistory && player.gameHistory.quizResults) {
                Object.entries(player.gameHistory.quizResults).forEach(([quizId, result]) => {
                    if (!quizResults[quizId]) {
                        quizResults[quizId] = {
                            quizName: result.quizTitle || quizId,
                            totalScore: 0,
                            totalPlayers: 0,
                            averageScore: 0
                        };
                    }
                    quizResults[quizId].totalScore += result.score;
                    quizResults[quizId].totalPlayers += 1;
                });
            }
        });

        // Calcul des moyennes
        const performanceData = Object.values(quizResults).map(quiz => ({
            quizName: quiz.quizName,
            averageScore: quiz.totalPlayers > 0 ? Math.round(quiz.totalScore / quiz.totalPlayers) : 0,
            totalPlayers: quiz.totalPlayers
        }));

        setQuizPerformance(performanceData);
    };

    // Calcul des données pour le graphique des performances par catégorie
    const calculateCategoryPerformance = (players) => {
        const categories = {};

        players.forEach(player => {
            if (player.trainingStats && player.trainingStats.categoryPerformance) {
                Object.entries(player.trainingStats.categoryPerformance).forEach(([category, stats]) => {
                    if (!categories[category]) {
                        categories[category] = {
                            name: category,
                            totalCorrect: 0,
                            totalAsked: 0
                        };
                    }
                    categories[category].totalCorrect += stats.correct || 0;
                    categories[category].totalAsked += stats.asked || 0;
                });
            }
        });

        const categoryData = Object.values(categories).map(cat => ({
            name: cat.name,
            successRate: cat.totalAsked > 0 ? Math.round((cat.totalCorrect / cat.totalAsked) * 100) : 0,
            questionsCount: cat.totalAsked
        }));

        setCategoryPerformance(categoryData);
    };

    // Formatage de la date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Jamais';

        // Gestion des timestamps Firebase (objets avec propriété seconds)
        let date;
        if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Couleurs pour les graphiques
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title="Statistiques des Joueurs"/>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="flex flex-col space-y-6">
                    {/* En-tête avec recherche et tri */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Statistiques des Joueurs</h1>
                                <p className="text-gray-500">Analysez les performances et l'engagement des joueurs</p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Rechercher un joueur..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                    <div
                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                        </svg>
                                    </div>
                                </div>

                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value="name">Trier par nom</option>
                                    <option value="totalScore">Trier par score total</option>
                                    <option value="totalGames">Trier par parties jouées</option>
                                    <option value="lastPlayed">Trier par dernière partie</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Onglets */}
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="border-b border-gray-200">
                            <nav className="-mb-px flex" aria-label="Tabs">
                                <button
                                    onClick={() => setActiveTab('overview')}
                                    className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                        activeTab === 'overview'
                                            ? 'border-indigo-500 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Vue d'ensemble
                                </button>
                                <button
                                    onClick={() => setActiveTab('players')}
                                    className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                        activeTab === 'players'
                                            ? 'border-indigo-500 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Liste des joueurs
                                </button>
                                <button
                                    onClick={() => setActiveTab('charts')}
                                    className={`w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                        activeTab === 'charts'
                                            ? 'border-indigo-500 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Graphiques
                                </button>
                            </nav>
                        </div>

                        <div className="p-6">
                            {/* Vue d'ensemble */}
                            {activeTab === 'overview' && (
                                <div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                        {/* Statistiques */}
                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                                        <svg className="h-6 w-6 text-white" fill="none"
                                                             viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                                                        </svg>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                                Nombre de joueurs
                                                            </dt>
                                                            <dd className="flex items-baseline">
                                                                <div className="text-2xl font-semibold text-gray-900">
                                                                    {players.length}
                                                                </div>
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                                        <svg className="h-6 w-6 text-white" fill="none"
                                                             viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                                        </svg>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                                Score moyen
                                                            </dt>
                                                            <dd className="flex items-baseline">
                                                                <div className="text-2xl font-semibold text-gray-900">
                                                                    {players.length > 0
                                                                        ? Math.round(players.reduce((sum, player) => sum + (player.gameHistory?.totalScore || 0), 0) / players.length)
                                                                        : 0}
                                                                </div>
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                                        <svg className="h-6 w-6 text-white" fill="none"
                                                             viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                                                        </svg>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                                Parties jouées
                                                            </dt>
                                                            <dd className="flex items-baseline">
                                                                <div className="text-2xl font-semibold text-gray-900">
                                                                    {players.reduce((sum, player) => sum + (player.gameHistory?.totalGames || 0), 0)}
                                                                </div>
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                                        <svg className="h-6 w-6 text-white" fill="none"
                                                             viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2}
                                                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                        </svg>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                                Joueurs actifs (7j)
                                                            </dt>
                                                            <dd className="flex items-baseline">
                                                                <div className="text-2xl font-semibold text-gray-900">
                                                                    {players.filter(player => {
                                                                        if (!player.gameHistory?.lastPlayed) return false;
                                                                        let lastPlayed;
                                                                        if (player.gameHistory.lastPlayed.seconds) {
                                                                            lastPlayed = new Date(player.gameHistory.lastPlayed.seconds * 1000);
                                                                        } else {
                                                                            lastPlayed = new Date(player.gameHistory.lastPlayed);
                                                                        }
                                                                        const sevenDaysAgo = new Date();
                                                                        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                                                                        return lastPlayed >= sevenDaysAgo;
                                                                    }).length}
                                                                </div>
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Top 5 des joueurs */}
                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:px-6">
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Top 5 des
                                                    joueurs</h3>
                                                <p className="mt-1 max-w-2xl text-sm text-gray-500">Classés par score
                                                    total</p>
                                            </div>
                                            <div className="border-t border-gray-200">
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Joueur
                                                            </th>
                                                            <th scope="col"
                                                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Score
                                                            </th>
                                                            <th scope="col"
                                                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Parties
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                        {players
                                                            .sort((a, b) => (b.gameHistory?.totalScore || 0) - (a.gameHistory?.totalScore || 0))
                                                            .slice(0, 5)
                                                            .map((player) => (
                                                                <tr key={player.id}>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {player.profile?.displayName || 'Anonyme'}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                                        {player.gameHistory?.totalScore || 0}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                                        {player.gameHistory?.totalGames || 0}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Joueurs récents */}
                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="px-4 py-5 sm:px-6">
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Joueurs
                                                    récents</h3>
                                                <p className="mt-1 max-w-2xl text-sm text-gray-500">Dernières
                                                    activités</p>
                                            </div>
                                            <div className="border-t border-gray-200">
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Joueur
                                                            </th>
                                                            <th scope="col"
                                                                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Dernière partie
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                        {players
                                                            .filter(player => player.gameHistory?.lastPlayed)
                                                            .sort((a, b) => {
                                                                const dateA = new Date(a.gameHistory.lastPlayed.seconds ? a.gameHistory.lastPlayed.seconds * 1000 : a.gameHistory.lastPlayed);
                                                                const dateB = new Date(b.gameHistory.lastPlayed.seconds ? b.gameHistory.lastPlayed.seconds * 1000 : b.gameHistory.lastPlayed);
                                                                return dateB - dateA;
                                                            })
                                                            .slice(0, 5)
                                                            .map((player) => (
                                                                <tr key={player.id}>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                        {player.profile?.displayName || 'Anonyme'}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                                        {formatDate(player.gameHistory.lastPlayed)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Liste des joueurs */}
                            {activeTab === 'players' && (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Joueur
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Email
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Score total
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Parties jouées
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Dernière partie
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Taux de réussite
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredPlayers.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                                    Aucun joueur trouvé
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredPlayers.map((player) => {
                                                const successRate = player.trainingStats ?
                                                    (player.trainingStats.correctAnswers / player.trainingStats.questionsAnswered * 100) || 0 : 0;

                                                return (
                                                    <tr key={player.id}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {player.profile?.displayName || 'Anonyme'} <br/>
                                                            <i>{player.profile?.phoneNumber}</i>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {player.profile?.email || '-'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                            {player.gameHistory?.totalScore || 0}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                            {player.gameHistory?.totalGames || 0}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                            {formatDate(player.gameHistory?.lastPlayed)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        successRate > 75
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : successRate > 50
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {Math.round(successRate)}%
                                                                </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                                            <Link
                                                                to={`/admin/players/${player.id}`}
                                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                            >
                                                                Voir détails
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Graphiques */}
                            {activeTab === 'charts' && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Performance par quiz */}
                                    <div className="bg-white overflow-hidden shadow rounded-lg">
                                        <div className="px-4 py-5 sm:px-6">
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Performance par
                                                quiz</h3>
                                            <p className="mt-1 max-w-2xl text-sm text-gray-500">Score moyen par quiz</p>
                                        </div>
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="h-80">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={quizPerformance}
                                                              margin={{top: 20, right: 30, left: 20, bottom: 70}}>
                                                        <CartesianGrid strokeDasharray="3 3"/>
                                                        <XAxis dataKey="quizName" angle={-45} textAnchor="end"
                                                               height={70}/>
                                                        <YAxis/>
                                                        <Tooltip/>
                                                        <Legend/>
                                                        <Bar dataKey="averageScore" name="Score moyen" fill="#3b5998"/>
                                                        <Bar dataKey="totalPlayers" name="Nombre de joueurs"
                                                             fill="#f1c232"/>
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Performance par catégorie */}
                                    <div className="bg-white overflow-hidden shadow rounded-lg">
                                        <div className="px-4 py-5 sm:px-6">
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Performance par
                                                catégorie</h3>
                                            <p className="mt-1 max-w-2xl text-sm text-gray-500">Taux de réussite par
                                                catégorie de questions</p>
                                        </div>
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="h-80">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={categoryPerformance}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={true}
                                                            outerRadius={100}
                                                            fill="#8884d8"
                                                            dataKey="successRate"
                                                            nameKey="name"
                                                            label={({name, successRate}) => `${name}: ${successRate}%`}
                                                        >
                                                            {categoryPerformance.map((entry, index) => (
                                                                <Cell key={`cell-${index}`}
                                                                      fill={COLORS[index % COLORS.length]}/>
                                                            ))}
                                                        </Pie>
                                                        <Tooltip formatter={(value) => `${value}%`}/>
                                                        <Legend/>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
